import type {
  FunnelAssignRequest,
  FunnelAssignResponse,
  FunnelEvaluateStepRequest,
  FunnelEvaluateStepResponse,
  AcceptStepRequest,
  DeclineStepRequest,
  NavigateStepRequest,
  StepNavigationResponse,
  TemplateContentResponse,
} from './types';
import { funnelLogger } from './logger';
import { extractBlocksFromContent, normalizePageBlocks } from '@template-project/funnel-block-render';
import type { PageContext } from '@template-project/funnels-contract';

export interface FunnelClientConfig {
  apiBaseUrl: string;
  tenantId: string;
  checkoutMiddlewareUrl?: string;
  /**
   * Store identifier for telemetry tagging. Optional: the BFF ingest resolves
   * the authoritative store_id from the request's tenant scope and overrides
   * whatever the client sends — it does NOT reject a mismatch (see
   * ingest-analytics.use-case `resolveStoreId`). The provider falls back to
   * tenantId when this is unset so the telemetry batch schema's non-empty
   * store_id is satisfied.
   */
  storeId?: string;
}

/** Typed error that preserves the HTTP status code for callers to branch on. */
export class FunnelApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable error code parsed from the response body, when present. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'FunnelApiError';
  }
}

/**
 * Parse an error response into a human message + optional code. Understands both
 * RFC 7807 Problem+JSON (`{ title, detail, ... }`) and the funnels/BFF envelope
 * (`{ error, message, code, details }`), falling back to raw text.
 */
async function parseApiError(
  response: Response,
  label: string,
): Promise<{ message: string; code?: string }> {
  const text = await response.text().catch(() => '');
  let detail = text;
  let code: string | undefined;
  if (text) {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      const pick = (value: unknown): string | undefined =>
        typeof value === 'string' && value.trim() !== '' ? value : undefined;
      detail =
        pick(parsed.detail) ??
        pick(parsed.message) ??
        pick(parsed.title) ??
        pick(parsed.error) ??
        text;
      code = pick(parsed.code) ?? (typeof parsed.error === 'string' ? parsed.error : undefined);
    } catch {
      // Non-JSON body: keep the raw text as the detail.
    }
  }
  const message = `${label} ${response.status}${detail ? `: ${detail}` : ''}`;
  return { message, code };
}

async function funnelFetch<T>(
  baseUrl: string,
  tenantId: string,
  path: string,
  body: Record<string, unknown>,
  headers?: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const url = `${baseUrl.replace(/\/$/, '')}${path}?tenantId=${encodeURIComponent(tenantId)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const { message, code } = await parseApiError(response, 'Funnel API error');
    throw new FunnelApiError(response.status, message, code);
  }
  return response.json() as Promise<T>;
}

function nonNegativeInteger(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

function normalizePageContext(
  pageContext: PageContext | undefined,
  fallback: {
    pageType?: PageContext['pageType'];
    urlPath?: string;
    deviceType?: string;
    country?: string;
  },
): PageContext & {
  productId: string;
  productCollections: string[];
  productTags: string[];
  cartTotal: number;
  cartItemCount: number;
  cartProductIds: string[];
  cartCollections: string[];
  isReturning: boolean;
  pageViewCount: number;
  orderProductIds: string[];
  orderTotal: number;
} {
  return {
    ...(pageContext ?? {
      pageType: fallback.pageType ?? 'landing_page',
      url: fallback.urlPath ?? '/',
    }),
    pageType: pageContext?.pageType ?? fallback.pageType ?? 'landing_page',
    url: pageContext?.url ?? fallback.urlPath ?? '/',
    productId: pageContext?.productId ?? '',
    collectionId: pageContext?.collectionId,
    productCollections: pageContext?.productCollections ?? [],
    productTags: pageContext?.productTags ?? [],
    cartTotal: nonNegativeInteger(pageContext?.cartTotal),
    cartItemCount: nonNegativeInteger(pageContext?.cartItemCount),
    cartProductIds: pageContext?.cartProductIds ?? [],
    cartCollections: pageContext?.cartCollections ?? [],
    isReturning: pageContext?.isReturning ?? false,
    pageViewCount: nonNegativeInteger(pageContext?.pageViewCount ?? 1),
    orderProductIds: pageContext?.orderProductIds ?? [],
    orderTotal: nonNegativeInteger(pageContext?.orderTotal),
    deviceType: pageContext?.deviceType ?? fallback.deviceType,
    country: pageContext?.country ?? fallback.country,
  };
}

async function checkoutMiddlewareFetch<T>(
  baseUrl: string | undefined,
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  if (!baseUrl) {
    throw new Error('checkoutMiddlewareUrl is required for post-purchase upsell routing');
  }

  const url = `${baseUrl.replace(/\/$/, '')}${path}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const { message, code } = await parseApiError(response, 'Checkout middleware error');
    throw new FunnelApiError(response.status, message, code);
  }

  return response.json() as Promise<T>;
}

export interface FunnelExperimentAssignment {
  experimentId: string;
  variantKey: string;
  scope?: 'funnel' | 'step';
  stepId?: string | null;
  variantFunnelId?: string | null;
  variantStepId?: string | null;
}

export interface FunnelExperimentAssignResponse {
  assignments: FunnelExperimentAssignment[];
}

export interface FunnelClient {
  assignFunnel(req: FunnelAssignRequest, signal?: AbortSignal): Promise<FunnelAssignResponse>;
  assignExperiments(funnelId: string, sessionId: string, signal?: AbortSignal): Promise<FunnelExperimentAssignResponse>;
  evaluateStep(req: FunnelEvaluateStepRequest): Promise<FunnelEvaluateStepResponse>;
  acceptStep(req: AcceptStepRequest): Promise<StepNavigationResponse>;
  declineStep(req: DeclineStepRequest): Promise<StepNavigationResponse>;
  navigateStep(req: NavigateStepRequest): Promise<StepNavigationResponse>;
  acceptUpsell(req: { funnelId: string; stepId: string; orderId: string; productId?: string; variantId?: string }): Promise<StepNavigationResponse>;
  declineUpsell(req: { funnelId: string; stepId: string }): Promise<StepNavigationResponse>;
  fetchTemplate(templateId: string): Promise<TemplateContentResponse>;
}

export function createFunnelClient(config: FunnelClientConfig): FunnelClient {
  const { apiBaseUrl, tenantId, checkoutMiddlewareUrl } = config;
  const base = apiBaseUrl.replace(/\/$/, '');

  // TF-AUD-012: Template cache with TTL to honor "always live" guarantee.
  const TEMPLATE_CACHE_TTL_MS = 60_000; // 60 seconds
  const templateCache = new Map<string, { data: TemplateContentResponse; timestamp: number }>();
  const inflightRequests = new Map<string, Promise<TemplateContentResponse>>();

  // React.StrictMode double-invokes the mount effect that calls evaluateStep,
  // sending two identical requests for the same page view. The server's
  // once_per_session frequency cap is consumed by the phantom first request
  // before the user ever sees it, silently skipping ahead. Coalesce
  // overlapping identical calls into one real fetch, same pattern as
  // inflightRequests above.
  const evaluateStepInflight = new Map<string, Promise<FunnelEvaluateStepResponse>>();

  return {
    assignFunnel: (req, signal) =>
      funnelFetch<FunnelAssignResponse>(base, tenantId, '/v1/storefront/funnels/assign', {
        sessionId: req.sessionId,
        customerCurrency: req.customerCurrency,
        customerCountry: req.customerCountry,
        urlPath: req.urlPath,
        pageType: req.pageType,
        pageContext: normalizePageContext(req.pageContext, {
          pageType: req.pageType,
          urlPath: req.urlPath,
          deviceType: req.deviceType,
          country: req.customerCountry,
        }),
        deviceType: req.deviceType,
        referrerSource: req.referrerSource,
      }, undefined, signal),

    assignExperiments: (funnelId, sessionId, signal) =>
      funnelFetch<FunnelExperimentAssignResponse>(
        base,
        tenantId,
        `/v1/storefront/funnels/${encodeURIComponent(funnelId)}/experiments/assign`,
        { sessionId },
        undefined,
        signal,
      ),

    evaluateStep: (req) => {
      const body = {
        sessionId: req.sessionId,
        funnelId: req.funnelId,
        customerCurrency: req.customerCurrency,
        currentStepId: req.currentStepId,
        event: req.event,
        pageContext: normalizePageContext(req.pageContext, {}),
      };
      const key = JSON.stringify(body);
      const inflight = evaluateStepInflight.get(key);
      if (inflight) return inflight;

      const promise = funnelFetch<FunnelEvaluateStepResponse>(
        base,
        tenantId,
        '/v1/storefront/funnels/evaluate-step',
        body,
      ).finally(() => {
        evaluateStepInflight.delete(key);
      });
      evaluateStepInflight.set(key, promise);
      return promise;
    },

    acceptStep: (req) =>
      funnelFetch<StepNavigationResponse>(
        base,
        tenantId,
        `/v1/storefront/funnels/${req.funnelId}/steps/${req.stepId}/accept`,
        {
          sessionId: req.sessionId,
          action: req.action,
          offerId: req.offerId,
          productId: req.productId,
          variantId: req.variantId,
          quantity: req.quantity ?? 1,
          cartToken: req.cartToken,
        },
        req.idempotencyKey ? { 'Idempotency-Key': req.idempotencyKey } : undefined
      ),

    declineStep: (req) =>
      funnelFetch<StepNavigationResponse>(
        base,
        tenantId,
        `/v1/storefront/funnels/${req.funnelId}/steps/${req.stepId}/decline`,
        { sessionId: req.sessionId },
      ),

    // D-C: server-validated go_to_step. funnels-go authorizes the jump only when
    // the current step's listeners authored a go_to_step reaction to the target,
    // then returns the target experience in the same shape as accept/decline.
    navigateStep: (req) =>
      funnelFetch<StepNavigationResponse>(
        base,
        tenantId,
        `/v1/storefront/funnels/${req.funnelId}/steps/${req.stepId}/navigate`,
        { sessionId: req.sessionId, targetStepId: req.targetStepId },
      ),

    acceptUpsell: (req) =>
      checkoutMiddlewareFetch<StepNavigationResponse>(
        checkoutMiddlewareUrl,
        // Routes mount under /v1 in checkout-middleware; the base URL is the bare
        // host (same convention as /v1/checkout/*), so the prefix must be explicit.
        // Body keys are camelCase — the middleware reads `orderId`.
        `/v1/funnels/${req.funnelId}/steps/${req.stepId}/accept`,
        {
          orderId: req.orderId,
          productId: req.productId,
          variantId: req.variantId,
        },
      ),

    declineUpsell: (req) =>
      checkoutMiddlewareFetch<StepNavigationResponse>(
        checkoutMiddlewareUrl,
        `/v1/funnels/${req.funnelId}/steps/${req.stepId}/decline`,
      ),

    fetchTemplate: (templateId: string): Promise<TemplateContentResponse> => {
      const cached = templateCache.get(templateId);
      if (cached && (Date.now() - cached.timestamp) < TEMPLATE_CACHE_TTL_MS) {
        return Promise.resolve(cached.data);
      }
      // Evict expired entry
      if (cached) templateCache.delete(templateId);

      const inflight = inflightRequests.get(templateId);
      if (inflight) return inflight;

      const url = `${base}/v1/storefront/funnels/templates/${encodeURIComponent(templateId)}?tenantId=${encodeURIComponent(tenantId)}`;
      const promise = fetch(url, { method: 'GET' })
        .then(async (response) => {
          if (!response.ok) {
            const { message, code } = await parseApiError(response, 'Template API error');
            throw new FunnelApiError(response.status, message, code);
          }
          return response.json() as Promise<Record<string, unknown>>;
        })
        .then((raw) => {
          // TPL-001: Go API may wrap in { template: {...} } and uses `componentTree` + `definitionId`
          const t = (raw.template ?? raw) as Record<string, unknown>;
          const rawContent = t.componentTree ?? t.pageContent ?? null;
          const rawBlocks = extractBlocksFromContent(rawContent);

          if (!rawBlocks) {
            funnelLogger.warn('Funnel template payload has no renderable blocks', {
              templateId,
              reason: rawContent == null ? 'missing_page_content' : 'invalid_page_content_shape',
              hasComponentTree: t.componentTree != null,
              hasPageContent: t.pageContent != null,
            });
          }

          const pageContent = normalizePageBlocks(rawBlocks);

          const mapped: TemplateContentResponse = {
            id: String(t.id ?? ''),
            pageContent,
            displaySettings: (t.displaySettings ?? null) as TemplateContentResponse['displaySettings'],
          };
          return mapped;
        })
        .then((data) => {
          templateCache.set(templateId, { data, timestamp: Date.now() });
          return data;
        })
        .finally(() => {
          inflightRequests.delete(templateId);
        });

      inflightRequests.set(templateId, promise);
      return promise;
    },
  };
}
